import { FormInstance } from 'antd';
import { ValueField } from 'types';
import { NamePath } from '@xbcb/ui-types';
import { calculateValuePerUnit } from 'libs/calculateValuePerUnit';

type SetProductValueFieldProps = {
  form: FormInstance;
  unitField: ValueField;
  totalField: ValueField;
  productNamePath: NamePath;
  currencyNamePath?: NamePath;
  realTariffIndex?: number;
  shouldSetCurrency?: boolean;
  /**
   * The function used to calculate the value and path to set
   * @param input.form - The form instance
   * @param input.quantity - The quantity value
   * @param input.unitFieldNamePath - The name path of the unit field
   * @param input.totalFieldNamePath - The name path of the total field
   * @returns The path to the field, and the value to set for that field
   * @param input
   */
  calculateValueFunc: (input: {
    form: FormInstance;
    quantity: number | undefined;
    unitFieldNamePath: NamePath;
    totalFieldNamePath: NamePath;
  }) => { pathToSet: NamePath; value: number | undefined };
};

/**
 * Set the value of a product field based on the quantity,
 *  unit and total fields and a given value-calculation callback function
 *
 *  Sets the value and currency using the path and value returned by the calculateValueFunc
 */
export const setProductValueField = ({
  form,
  unitField,
  totalField,
  productNamePath,
  currencyNamePath,
  realTariffIndex,
  shouldSetCurrency = true,
  calculateValueFunc,
}: SetProductValueFieldProps): void => {
  if (realTariffIndex === undefined) return;
  // set name paths
  const unitFieldNamePath = [
    ...productNamePath,
    'lines',
    0,
    'tariffs',
    realTariffIndex,
    unitField,
  ];
  const totalFieldNamePath = [...productNamePath, totalField];

  // set intermediate values
  const quantity = form.getFieldValue([...productNamePath, 'quantity']);
  const { pathToSet, value } = calculateValueFunc({
    form,
    quantity,
    unitFieldNamePath,
    totalFieldNamePath,
  });

  // set final form values
  if (shouldSetCurrency && currencyNamePath) {
    const currency = form.getFieldValue(currencyNamePath);
    form.setFieldValue(pathToSet, value ? { value, currency } : undefined);
  } else if (value) {
    form.setFieldValue([...pathToSet, 'value'], value);
  }
};

export type ProductFieldSetterProps = Omit<
  SetProductValueFieldProps,
  'calculateValueFunc'
>;

/**
 * Set the unit value of a product based on the quantity and total value,
 *  dividing the total by the quantity
 *  @param {ProductFieldSetterProps} props - The props for setting the product value field, passed down to setProductValueField
 *  @return {void}
 */
export const setProductUnitValueField = (
  props: ProductFieldSetterProps,
): void =>
  setProductValueField({
    ...props,
    calculateValueFunc: ({
      form,
      totalFieldNamePath,
      unitFieldNamePath,
      quantity,
    }) => {
      const totalValue = form.getFieldValue([...totalFieldNamePath, 'value']);
      const value = calculateValuePerUnit({ quantity, total: totalValue });
      return { value, pathToSet: unitFieldNamePath };
    },
  });

/**
 * Set the total value of a product based on the quantity and unit value,
 *  multiplying the unit by the quantity
 *  @param {ProductFieldSetterProps} props - The props for setting the product value field, passed down to setProductValueField
 *  @return {void}
 */
export const setProductTotalValueField = (
  props: ProductFieldSetterProps,
): void =>
  setProductValueField({
    ...props,
    calculateValueFunc: ({
      form,
      totalFieldNamePath,
      unitFieldNamePath,
      quantity,
    }) => {
      const unitValue = form.getFieldValue([...unitFieldNamePath, 'value']);
      const value =
        quantity === undefined || unitValue === undefined
          ? undefined
          : unitValue * quantity;
      return { value, pathToSet: totalFieldNamePath };
    },
  });
